/**
 * The works a visitor has hearted, kept in this browser only.
 *
 * No account and no backend: a heart is a bookmark, not an order, and asking
 * someone to sign in to remember a picture would lose most of them. The list
 * lives in localStorage, which can be missing, full or blocked (private
 * windows, strict privacy settings) — every read and write is guarded, and
 * without storage the hearts still work for as long as the tab is open.
 *
 * Artwork ids, not slugs: a slug can be renamed for search, the id cannot.
 */

export const WISHLIST_KEY = "fd-art-wishlist";

/** More than anyone hearts by hand; keeps a corrupted value from growing without end. */
export const WISHLIST_LIMIT = 200;

/** Whatever was stored, as a clean list: strings only, no repeats, capped. */
export function parseWishlist(raw: string | null | undefined): string[] {
  if (!raw) return [];
  let value: unknown;
  try {
    value = JSON.parse(raw);
  } catch {
    return [];
  }
  if (!Array.isArray(value)) return [];
  const ids = value.filter((x): x is string => typeof x === "string" && x.length > 0);
  return [...new Set(ids)].slice(0, WISHLIST_LIMIT);
}

/** The list with `id` added (to the front, newest first) or removed. */
export function toggleWishlist(list: string[], id: string): string[] {
  if (list.includes(id)) return list.filter((x) => x !== id);
  return [id, ...list].slice(0, WISHLIST_LIMIT);
}

type Storage = Pick<globalThis.Storage, "getItem" | "setItem">;

/**
 * A small store over one storage key, shaped for useSyncExternalStore.
 *
 * The snapshot is the same array until the list changes, which is what
 * useSyncExternalStore needs to avoid re-rendering forever. Other tabs are
 * followed through the "storage" event, so hearting a work in one tab shows
 * in the other.
 */
export function createWishlistStore(getStorage: () => Storage | null) {
  const listeners = new Set<() => void>();
  // Used when storage refuses a write, so the heart still sticks in this tab.
  let memory: string[] | null = null;
  let snapshot: string[] = [];
  let lastRaw: string | null | undefined;

  const read = (): string[] => {
    if (memory) return memory;
    let raw: string | null = null;
    try {
      raw = getStorage()?.getItem(WISHLIST_KEY) ?? null;
    } catch {
      raw = null;
    }
    if (raw !== lastRaw) {
      lastRaw = raw;
      snapshot = parseWishlist(raw);
    }
    return snapshot;
  };

  const emit = () => listeners.forEach((listener) => listener());

  return {
    getSnapshot: read,
    subscribe(listener: () => void) {
      listeners.add(listener);
      const onStorage = (event: StorageEvent) => {
        if (event.key === null || event.key === WISHLIST_KEY) listener();
      };
      if (typeof window !== "undefined") window.addEventListener("storage", onStorage);
      return () => {
        listeners.delete(listener);
        if (typeof window !== "undefined") window.removeEventListener("storage", onStorage);
      };
    },
    toggle(id: string) {
      const next = toggleWishlist(read(), id);
      try {
        const storage = getStorage();
        if (!storage) throw new Error("no storage");
        const raw = JSON.stringify(next);
        storage.setItem(WISHLIST_KEY, raw);
        memory = null;
        lastRaw = raw;
        snapshot = next;
      } catch {
        memory = next;
      }
      emit();
    },
  };
}

const EMPTY: string[] = [];

/** The browser's store; null on the server and wherever localStorage is barred. */
export const wishlistStore = createWishlistStore(() => {
  try {
    return typeof window === "undefined" ? null : window.localStorage;
  } catch {
    return null;
  }
});

/** What the server renders: nothing hearted, so the first client render agrees. */
export const getServerWishlist = () => EMPTY;
